import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TEAM } from "@/constants/testIds";

function initialsOf(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

function TeamPortrait({ member }) {
  if (member.image_url) {
    return (
      <img
        src={member.image_url}
        alt={member.name}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
      />
    );
  }
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#12223d] via-[#0b1424] to-black">
      <div className="stripe-blue absolute inset-0 opacity-10" />
      <div className="relative font-oswald text-7xl font-bold uppercase tracking-widest text-white/90 sm:text-8xl">
        {initialsOf(member.name)}
      </div>
    </div>
  );
}

function SoloTeamCard({ member }) {
  return (
    <article
      data-testid="team-card-solo"
      className="lift group relative grid grid-cols-1 overflow-hidden rounded-sm border border-white/10 bg-[#151b26] md:grid-cols-5"
    >
      <div className="relative aspect-[3/4] md:col-span-2 md:aspect-auto">
        <TeamPortrait member={member} />
      </div>
      <div className="flex flex-col justify-center p-6 md:col-span-3 md:p-10">
        <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#ffd60a]">
          {member.role}
        </div>
        <div className="mt-2 font-oswald text-3xl font-bold uppercase tracking-wider text-white sm:text-4xl">
          {member.name}
        </div>
        <div className="mt-4 h-[2px] w-16 bg-[#1f63c9]" />
        <div className="mt-4 space-y-3 text-sm leading-relaxed text-gray-300 sm:text-base">
          {(member.bio || "").split(/\n\s*\n/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </div>
    </article>
  );
}

function TeamCard({ member }) {
  return (
    <article
      className="lift group relative aspect-[3/4] overflow-hidden rounded-sm border border-white/10 bg-[#151b26]"
    >
      <TeamPortrait member={member} />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent p-5">
        <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#ffd60a]">{member.role}</div>
        <div className="mt-1 font-oswald text-2xl font-bold uppercase tracking-wider text-white">{member.name}</div>
        <p className="mt-2 line-clamp-3 text-sm text-gray-300">{member.bio}</p>
      </div>
    </article>
  );
}

export default function TeamSection() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  if (!team.length) return null;
  const solo = team.length === 1;

  return (
    <section id="team" className="relative py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-6">
          <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">
            {solo ? "Meet Your Meteorologist" : "Meet the Crew"}
          </div>
          <h2 className="font-oswald text-2xl font-bold uppercase tracking-wider text-white sm:text-3xl lg:text-4xl">
            {solo ? "On the Air" : "Your Meteorologists"}
          </h2>
        </div>

        {solo ? (
          <div data-testid={TEAM.grid}>
            <SoloTeamCard member={team[0]} />
          </div>
        ) : (
          <div data-testid={TEAM.grid} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {team.map((m) => (
              <TeamCard key={m.id} member={m} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
